import { useEffect } from "react";

/**
 * Keep a page's data live for collaborative use ("one person edits, the other sees
 * the change without refreshing"). Polls every `intervalMs` while the tab is visible
 * and immediately refreshes when the tab regains focus. Pauses polling when the tab
 * is hidden to save battery / API load.
 *
 * @param {Function} loadFn — the async function to refresh data. Should be stable
 *                            (wrap in useCallback).
 * @param {number}   intervalMs — poll interval in ms (default 8000).
 */
export function useLiveSync(loadFn, intervalMs = 8000) {
  useEffect(() => {
    let interval;
    const start = () => {
      if (document.visibilityState !== "visible") return;
      if (interval) return;
      interval = setInterval(loadFn, intervalMs);
    };
    const stop = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") { loadFn(); start(); }
      else stop();
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", loadFn);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", loadFn);
    };
  }, [loadFn, intervalMs]);
}
